import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import ReactLoading from 'react-loading';
import { getSurveyMetrics } from 'utils/api';
import Sidebar from 'components/Common/Sidebar';
import { Toast } from 'components/Toast';
import styles from './Survey.module.scss';

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#FF7C7C', '#8DD1E1', '#D084D0',
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className={styles.chartTooltip}>
        <p className={styles.label}>{`${data.name || 'Not specified'}: ${data.value}`}</p>
      </div>
    );
  }
  return null;
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null; // Don't show labels for slices less than 5%

  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize="12"
      fontWeight="bold"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default function Survey() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getSurveyMetrics();
        setData(response);
        setError(null);
      } catch (err) {
        setError('Failed to load survey metrics');
        console.error('Error fetching survey metrics:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="main survey">
        <Sidebar />
        <div className="right-part">
          <Toast />
          <div className="full-screen-loading">
            <ReactLoading type="cylon" color="#ff9c34" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main survey">
        <Sidebar />
        <div className="right-part">
          <Toast />
          <div className="title">Survey Metrics</div>
          <div className={styles.errorMessage}>
            <h3>Error</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const jobChartData = data?.jobCounts?.map((item, index) => ({
    name: item.job || 'Not specified',
    value: item.count,
    color: COLORS[index % COLORS.length],
  })) || [];

  const platformChartData = data?.fromPlatformCounts?.map((item, index) => ({
    name: item.fromPlatform || 'Not specified',
    value: item.count,
    color: COLORS[index % COLORS.length],
  })) || [];

  const totalJobs = jobChartData.reduce((sum, item) => sum + item.value, 0);
  const totalPlatforms = platformChartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="main survey">
      <Sidebar />
      <div className="right-part">
        <Toast />
        <div className="title">Survey Metrics</div>
        <div className={styles.pageDescription}>User distribution by job and platform</div>

        <div className={styles.surveyMetrics}>
          <div className={styles.metricsGrid}>
            <div className={styles.metricCard}>
              <div className={styles.metricHeader}>
                <h3>Users by Job</h3>
                <span className={styles.totalCount}>
                  Total:
                  {totalJobs}
                </span>
              </div>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={jobChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {jobChartData.map((entry) => (
                        <Cell key={`job-cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value, entry) => `${value} (${entry.payload.value})`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className={styles.metricCard}>
              <div className={styles.metricHeader}>
                <h3>Users by Platform</h3>
                <span className={styles.totalCount}>
                  Total:
                  {totalPlatforms}
                </span>
              </div>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={platformChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {platformChartData.map((entry) => (
                        <Cell key={`platform-cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value, entry) => `${value} (${entry.payload.value})`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {data && (
            <div className={styles.metricsSummary}>
              <div className={styles.summarySection}>
                <h4>Job Distribution</h4>
                <div className={styles.summaryList}>
                  {jobChartData.map((item) => (
                    <div key={`job-summary-${item.name}`} className={styles.summaryItem}>
                      <div
                        className={styles.colorIndicator}
                        style={{ backgroundColor: item.color }}
                      />
                      <span className={styles.label}>{item.name}</span>
                      <span className={styles.count}>{item.value}</span>
                      <span className={styles.percentage}>
                        (
                        {((item.value / totalJobs) * 100).toFixed(1)}
                        %)
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.summarySection}>
                <h4>Platform Distribution</h4>
                <div className={styles.summaryList}>
                  {platformChartData.map((item) => (
                    <div key={`platform-summary-${item.name}`} className={styles.summaryItem}>
                      <div
                        className={styles.colorIndicator}
                        style={{ backgroundColor: item.color }}
                      />
                      <span className={styles.label}>{item.name}</span>
                      <span className={styles.count}>{item.value}</span>
                      <span className={styles.percentage}>
                        (
                        {((item.value / totalPlatforms) * 100).toFixed(1)}
                        %)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
